import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Tooltip, TextField, MenuItem,
    InputAdornment, CircularProgress, useTheme, useMediaQuery,
    Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import api from '../services/api';

const MESES = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const MESES_FULL = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function StatusChip({ status }) {
    switch (status) {
        case 'PENDENTE':   return <Chip label="Pendente"  color="warning" size="small" sx={{ fontWeight: 'bold' }} />;
        case 'ACEITA':     return <Chip label="Aceita"    color="success" size="small" sx={{ fontWeight: 'bold' }} />;
        case 'RECUSADA':   return <Chip label="Recusada"  color="error"   size="small" sx={{ fontWeight: 'bold' }} />;
        case 'EXPIRADA':   return <Chip label="Expirada"  color="default" size="small" sx={{ fontWeight: 'bold' }} />;
        default:           return <Chip label={status}    size="small" />;
    }
}

export default function HistoricoRenovacoes() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [renovacoes, setRenovacoes] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filtroPaciente, setFiltroPaciente] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('Todos');
    const [filtroMes, setFiltroMes] = useState('');
    const [filtroAno, setFiltroAno] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        api.get('/renovacao-assinatura')
            .then(res => { const d = res.data; setRenovacoes(Array.isArray(d) ? d : (d?.content ?? [])); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const hasActiveFilters = filtroStatus !== 'Todos' || filtroMes !== '' || filtroAno !== '';

    const filteredData = useMemo(() => {
        return renovacoes.filter(r => {
            const matchesPaciente = filtroPaciente === '' ||
                (r.nomePaciente || '').toLowerCase().includes(filtroPaciente.toLowerCase());
            const matchesStatus = filtroStatus === 'Todos' || r.status === filtroStatus;
            const matchesMes = filtroMes === '' || r.mesReferencia === parseInt(filtroMes);
            const matchesAno = filtroAno === '' || r.anoReferencia === parseInt(filtroAno);
            return matchesPaciente && matchesStatus && matchesMes && matchesAno;
        });
    }, [renovacoes, filtroPaciente, filtroStatus, filtroMes, filtroAno]);

    const handleLimparFiltros = () => {
        setFiltroStatus('Todos');
        setFiltroMes('');
        setFiltroAno('');
    };

    const gerarLinkWhatsApp = (r) => {
        const url = `${window.location.origin}/renovacao/${r.token}`;
        const msg = encodeURIComponent(
            `Olá, ${r.nomePaciente}! Sua assinatura do plano "${r.nomePlano}" para ${MESES_FULL[r.mesReferencia]}/${r.anoReferencia} aguarda renovação. Acesse: ${url}`
        );
        window.open(`https://wa.me/?text=${msg}`, '_blank');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AutorenewIcon sx={{ color: '#1ABB9C', fontSize: 32 }} />
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                    Histórico de Renovações
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, bgcolor: '#fff', borderRadius: 2 }}>
                {/* Barra de busca + filtros */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Pesquisar por paciente..."
                        variant="outlined"
                        fullWidth
                        value={filtroPaciente}
                        onChange={e => setFiltroPaciente(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                        }}
                    />
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                        <TextField select size="small" label="Status" value={filtroStatus}
                            onChange={e => setFiltroStatus(e.target.value)} sx={{ minWidth: 140, bgcolor: '#fff' }}>
                            <MenuItem value="Todos">Todos</MenuItem>
                            <MenuItem value="PENDENTE">Pendente</MenuItem>
                            <MenuItem value="ACEITA">Aceita</MenuItem>
                            <MenuItem value="RECUSADA">Recusada</MenuItem>
                            <MenuItem value="EXPIRADA">Expirada</MenuItem>
                        </TextField>
                        <TextField select size="small" label="Mês" value={filtroMes}
                            onChange={e => setFiltroMes(e.target.value)} sx={{ minWidth: 120, bgcolor: '#fff' }}>
                            <MenuItem value="">Todos</MenuItem>
                            {MESES.slice(1).map((m, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" label="Ano" type="number" value={filtroAno}
                            onChange={e => setFiltroAno(e.target.value)}
                            inputProps={{ min: 2020, max: 2099 }}
                            sx={{ minWidth: 100, bgcolor: '#fff' }} />
                        {hasActiveFilters && (
                            <Box
                                component="span"
                                onClick={handleLimparFiltros}
                                sx={{ fontSize: 13, color: 'error.main', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'center' }}
                            >
                                Limpar
                            </Box>
                        )}
                    </Box>
                </Collapse>

                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                    {filteredData.length} de {renovacoes.length} registro(s)
                    {(filtroPaciente || hasActiveFilters) && ' • Filtros aplicados'}
                </Typography>

                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell><strong>Paciente</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Plano</strong></TableCell>
                                <TableCell><strong>Referência</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Valor</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Vencimento</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Expira em</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.length > 0 ? filteredData.map(r => (
                                <TableRow key={r.idRenovacao} sx={{ '&:hover': { bgcolor: '#fcfcfc' } }}>
                                    <TableCell sx={{ fontWeight: 500 }}>{r.nomePaciente || '—'}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{r.nomePlano || '—'}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {MESES[r.mesReferencia]}/{r.anoReferencia}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        R$ {Number(r.valorCobrado || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {r.proximoVencimento
                                            ? new Date(r.proximoVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                            : '—'}
                                    </TableCell>
                                    <TableCell><StatusChip status={r.status} /></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        <Typography
                                            variant="body2"
                                            color={r.status === 'PENDENTE' && r.dataExpiracao && new Date(r.dataExpiracao) < new Date() ? 'error' : 'text.secondary'}
                                        >
                                            {r.dataExpiracao
                                                ? new Date(r.dataExpiracao).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                                : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {r.status === 'PENDENTE' && (
                                            <Tooltip title="Enviar link via WhatsApp">
                                                <IconButton size="small" sx={{ color: '#25D366' }} onClick={() => gerarLinkWhatsApp(r)}>
                                                    <WhatsAppIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhum registro encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
